import type { FC, ReactEventHandler } from 'react';

import { useScrollContext } from '@ipen-portal/plugin-common-react';
import { CircularProgress } from '@mui/material';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { ResizeObserver } from 'resize-observer';
import { tss } from 'tss-react/mui';

import { IPEN_ENHANCED } from './constants';
import { useAnchorRewriter } from './hooks/useAnchorRewriter';
import { useSrcRewriter } from './hooks/useSrcRewriter';
import { getAnchors } from './utils/getAnchors';
import { getSrcElements } from './utils/getSrcElements';

const useStyles = tss.create(({ theme }) => ({
  iframe: {
    border: 0,
    position: 'sticky',
    width: '100%',
  },
  loadingContainer: {
    alignItems: 'center',
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
    justifyContent: 'center',
    padding: theme.spacing(2),
  },
  scrollContainer: {
    overflow: 'clip',
    width: '100%',
  },
}));

type Props = {
  contextPath: string;
  hideHeader?: boolean;
  hideTabs?: boolean;
  padding?: number;
  path: string;
};

let preventScrollEventFromIframe = false;

export const MkDocsIFrame: FC<Props> = ({
  contextPath,
  hideHeader,
  hideTabs,
  padding = 0,
  path,
}) => {
  const { classes } = useStyles();
  const [loading, setLoading] = useState(true);
  const anchorRewriter = useAnchorRewriter();
  const srcRewriter = useSrcRewriter();
  const location = useLocation();
  const {
    headerOffset,
    setScrollPosition,
    stickAt,
    top: scrollTop,
  } = useScrollContext();

  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [iframeHeight, setIframeHeight] = useState<number>(0);

  const contentWindow = iframeRef.current?.contentWindow;

  // the offset of the iframe from the top of the scroll container.
  // this is the distance of the clip-window (the one around the iframe) to the top of the outer scroll area.
  // The distance will e.g. be the height of the mushroom header, which should scroll away with the page
  const heightOfContentAboveIFrame =
    iframeRef.current?.parentElement?.offsetTop ?? 0;
  // this can be a negative value, which includes all the content above the iframe that should be scrolled away,
  // before we start scrolling the iframe
  const scrollOffset = stickAt - heightOfContentAboveIFrame;

  useEffect(() => {
    if (!contentWindow || scrollOffset === undefined)
      return () => {
        /* ignore */
      };
    const onScroll = () => {
      if (preventScrollEventFromIframe) {
        preventScrollEventFromIframe = false;
        return;
      }

      setScrollPosition({
        left: contentWindow.scrollX,
        top: contentWindow.scrollY - scrollOffset,
      });
    };
    contentWindow.addEventListener('scroll', onScroll);
    return () => {
      contentWindow.removeEventListener('scroll', onScroll);
    };
  }, [contentWindow, scrollOffset, setScrollPosition]);

  useEffect(() => {
    if (!contentWindow || scrollOffset === undefined) return;

    if (scrollTop === undefined) {
      // update the parent scroll position to match the initial scroll position of the iframe
      setScrollPosition({
        left: contentWindow.scrollX,
        top: contentWindow.scrollY - scrollOffset,
      });
      return;
    }
    // disable scroll event been fired from iframe
    preventScrollEventFromIframe = scrollTop > 0;
    // update the iframe scroll position to match the parent scroll position
    contentWindow.scroll(0, scrollTop + scrollOffset);
  }, [contentWindow, scrollTop, scrollOffset, setScrollPosition]);

  // handler to detect when the iframe height changes
  const resizeObserver = useMemo(
    () =>
      new ResizeObserver(e => {
        const [entry] = e;
        if (entry) setIframeHeight(entry.contentRect.height);
      }),
    [setIframeHeight],
  );

  const onLoad: ReactEventHandler<HTMLIFrameElement> = useCallback(
    e => {
      // remove the resize observer from the previous iframe
      resizeObserver.disconnect();

      const iframeDocument = (e.target as HTMLIFrameElement).contentDocument;
      if (!iframeDocument) {
        return;
      }

      if (hideTabs) {
        const results =
          iframeDocument.querySelectorAll<HTMLElement>('.md-tabs');
        results.forEach(elem => {
          elem.style.display = 'none';
        });
      }

      if (hideHeader) {
        /**
         * This makes sure that the 3rd level navigation from mkdocs is visible on small screens
         */
        iframeDocument.head.insertAdjacentHTML(
          'beforeend',
          `
          <style>
            @media (min-width: 1220px) {
              header.md-header {
                display: none;
              }
            }
          </style>
          `,
        );
      }

      for (const anchor of getAnchors(iframeDocument)) anchorRewriter(anchor);
      for (const src of getSrcElements(iframeDocument)) srcRewriter(src);

      /**
       * This handler is used to intercept clicks on dynamic links and navigate to the correct page in the parent window
       * (it will be used for the techdocs search)
       * @param ev the event
       */
      const onDynamicLink = (ev: MouseEvent) => {
        const elem = ev
          .composedPath()
          .find(
            (et: EventTarget): et is HTMLAnchorElement =>
              et instanceof HTMLAnchorElement,
          );
        if (elem && elem.getAttribute(IPEN_ENHANCED) !== 'true') {
          // here we got dynamic links
          anchorRewriter(elem);
        }
      };

      iframeDocument.addEventListener('click', onDynamicLink);
      iframeDocument.addEventListener('auxclick', onDynamicLink);
      resizeObserver.observe(iframeDocument.body);

      setLoading(false);
    },
    [anchorRewriter, resizeObserver, hideTabs, hideHeader, srcRewriter],
  );

  // ensure index.html is loaded if no file is specified
  const iframeSrc =
    contextPath + path.replace(/^(.*?\/)($|\?|#)/, '$1index.html$2');

  // noinspection XmlDeprecatedElement -> scrolling="no" is deprecated, but there is no working alternative
  return (
    <>
      {loading && (
        <div className={classes.loadingContainer}>
          <CircularProgress />
        </div>
      )}

      <div
        className={classes.scrollContainer}
        style={{
          height: iframeHeight + stickAt,
        }}
      >
        <iframe
          className={classes.iframe}
          data-testid="mkdocs-iframe"
          key={contextPath + path}
          onLoad={onLoad}
          ref={iframeRef}
          scrolling="no"
          src={iframeSrc + location.search + location.hash}
          style={{
            height: `calc(100vh - ${stickAt + padding}px)`,
            top: headerOffset,
          }}
          title="Documentation"
        />
      </div>
    </>
  );
};
